import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import {CLStore} from 'common/src/redux/store';
import {
  AiContentVariationColumnDataType,
  RemoteAiContentProject,
  RemoteAiContentVariationColumn,
  RemoteAiContentContentFeedColumnParams,
  RemoteAiContentGenerationSetup,
  RemoteTemplateSummary,
} from '../../aiContentProject.types';
import {ProductShotType} from 'common/src/modules/aiImages/aiImages.enums';
import {
  RemoteAiContentItem,
  RemoteAiContentOutputDeliverable,
} from '../../aiContentItem.types';
import {
  CreativeTemplateType,
  templateTypeNamesByValue,
} from 'common/src/modules/creativeTemplates/creativeTemplates.types';
import {DynamicParametersType} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {Persona} from 'common/src/modules/brandGuidelines/brandGuidelines.types';
import {Folder} from 'common/src/modules/Assets/Assets.types';
import getAllAssetsFromTree from 'common/src/modules/Assets/utils/getAllAssetsFromTree';
import {taxonomy} from '@cl/shared';
import taxonomize from 'common/src/modules/taxonomy/utils/taxonomize';
import {TaxonomyType} from 'common/src/modules/taxonomy/taxonomy.enums';

type PersonaInfo = {
  id: number;
  name: string | null;
};

type AssetInfo = {
  id: number;
  name: string;
};

type AiContentProjectContextData = {
  project: RemoteAiContentProject | null;
  items: RemoteAiContentItem[];
  projectPersonas: PersonaInfo[];
  assetLookup: Record<number, AssetInfo>;
};

const VARIATION_COLUMN_TYPE_LABELS: Record<AiContentVariationColumnDataType, string> = {
  [AiContentVariationColumnDataType.Text]: 'Text',
  [AiContentVariationColumnDataType.Number]: 'Number',
  [AiContentVariationColumnDataType.Color]: 'Color',
  [AiContentVariationColumnDataType.Image]: 'Image',
  [AiContentVariationColumnDataType.Video]: 'Video',
  [AiContentVariationColumnDataType.Persona]: 'Persona',
  [AiContentVariationColumnDataType.Language]: 'Language',
};

const DYNAMIC_PARAM_TYPE_LABELS: Record<DynamicParametersType, string> = {
  [DynamicParametersType.Text]: 'Text',
  [DynamicParametersType.Color]: 'Color',
  [DynamicParametersType.FloatNumber]: 'Number',
  [DynamicParametersType.Image]: 'Image',
  [DynamicParametersType.Video]: 'Video',
  [DynamicParametersType.Boolean]: 'Boolean',
  [DynamicParametersType.Selector]: 'Selector',
  [DynamicParametersType.Audio]: 'Audio',
  [DynamicParametersType.Link]: 'Link',
  [DynamicParametersType.Widget]: 'Widget',
  [DynamicParametersType.Font]: 'Font',
  [DynamicParametersType.QR]: 'QR Code',
  [DynamicParametersType.Subtitles]: 'Subtitles',
};


function getItemGenerationStatus(item: RemoteAiContentItem): string {
  const hasPromptGenerationTime = !!item.promptGenerationTime;

  const hasAnyDeliverableGenerationTimestamp = Object.values(
    item.outputDeliverables ?? {},
  ).some((deliverable: RemoteAiContentOutputDeliverable) => {
    const byAdSize = deliverable.byAdSize;
    if (!byAdSize) return false;

    return Object.values(byAdSize).some(
      (adSizeOutput) =>
        !!adSizeOutput.generationTimestamp &&
        (!!adSizeOutput.imageSpec || !!adSizeOutput.videoSpec),
    );
  });

  if (hasAnyDeliverableGenerationTimestamp) {
    return 'Assets Generated';
  }

  if (hasPromptGenerationTime) {
    return 'Prompts Generated';
  }

  return 'Not Started';
}

function formatBriefSection(
  project: RemoteAiContentProject,
  projectPersonas: PersonaInfo[],
): string {
  const brief = project.brief;
  const lines: string[] = ['### Project Brief'];

  lines.push(`- Objective: ${brief?.objective || 'Not set'}`);
  lines.push(`- Description: ${brief?.description || 'Not set'}`);
  lines.push(`- Apply Brand Guidelines: ${brief?.applyBrandGuidelines ? 'Yes' : 'No'}`);

  // Format personas with ID and name
  if (projectPersonas.length > 0) {
    const personaList = projectPersonas
      .map((p) => `${p.name || 'Unnamed'} (ID: ${p.id})`)
      .join(', ');
    lines.push(`- Personas: ${projectPersonas.length} assigned - ${personaList}`);
  } else {
    lines.push(`- Personas: None assigned`);
  }

  return lines.join('\n');
}

function formatInputParametersSection(project: RemoteAiContentProject): string {
  const columns = project.variationInputSchema?.columns || [];

  if (columns.length === 0) {
    return '### Input Parameters (Variation Columns)\nNo input parameters defined.';
  }

  const lines: string[] = ['### Input Parameters (Variation Columns)'];
  lines.push('The following input parameters are defined for item creation:');
  lines.push('| # | Name | Type | Required | Purpose |');
  lines.push('|---|------|------|----------|---------|');

  columns.forEach((column: RemoteAiContentVariationColumn, index: number) => {
    const typeName = VARIATION_COLUMN_TYPE_LABELS[column.dataType] || 'Unknown';
    lines.push(
      `| ${index + 1} | ${column.name || 'Unnamed'} | ${typeName} | ${column.isRequired ? 'Yes' : 'No'} | ${column.usedFor || 'Not specified'} |`,
    );
  });

  return lines.join('\n');
}

function formatTemplateSection(project: RemoteAiContentProject): string {
  const templateSummary = project.templateSummary;

  if (!templateSummary) {
    return '### Template Information\nNo template selected.';
  }

  const typeName =
    templateSummary.type != null
      ? templateTypeNamesByValue[templateSummary.type as CreativeTemplateType]
      : 'Unknown';

  const lines: string[] = ['### Template Information'];
  lines.push(`- Template: ${templateSummary.name} (ID: ${templateSummary.id})`);
  lines.push(`- Type: ${typeName}`);

  const adSizes = project.templateMetadata?.adSizes?.join(', ') || 'None';
  lines.push(`- Supported Ad Sizes: ${adSizes}`);

  return lines.join('\n');
}

function getInputColumnsFromSetup(setup: RemoteAiContentGenerationSetup | null | undefined): string {
  if (!setup) return 'None';

  const columnNames: string[] = [];

  if (setup.textGeneration?.usedInputColumns) {
    setup.textGeneration.usedInputColumns.forEach((col) => columnNames.push(col.columnName));
  }

  if (setup.imageAiGeneration?.usedInputColumns) {
    setup.imageAiGeneration.usedInputColumns.forEach((col) => columnNames.push(col.columnName));
  }

  if (setup.imageSmartCrop?.inputColumnName) {
    columnNames.push(setup.imageSmartCrop.inputColumnName);
  }

  if (setup.productShotGeneration) {
    if (setup.productShotGeneration.productImageInputColumnName) {
      columnNames.push(setup.productShotGeneration.productImageInputColumnName);
    }

    if (setup.productShotGeneration.ScenePromptInputColumnName) {
      setup.productShotGeneration.ScenePromptInputColumnName.forEach((col) =>
        columnNames.push(col.columnName),
      );
    }

    if (setup.productShotGeneration.OptionalSceneReferenceImageInputColumnName) {
      columnNames.push(setup.productShotGeneration.OptionalSceneReferenceImageInputColumnName);
    }
  }

  return columnNames.length > 0 ? columnNames.join(', ') : 'None';
}

function formatGenerationSetupSummary(
  setup: RemoteAiContentGenerationSetup | null | undefined,
): string {
  if (!setup) return 'Not configured';

  const parts: string[] = [];

  if (setup.textGeneration) {
    const mode = setup.textGeneration.textConsistencyMode === 1 ? 'Adapted per size' : 'Identical across sizes';
    const adapted = setup.textGeneration.adaptedAdSizes?.length
      ? ` (except ${setup.textGeneration.adaptedAdSizes.join(', ')})`
      : '';
    parts.push(`Text consistency: ${mode}${adapted}`);
  }

  if (setup.imageAiGeneration) {
    const gen = setup.imageAiGeneration;
    let description = `Image model used: ${gen.vendorService ?? 'N/A'}`;

    if (gen.useSharedIntermediateImage) {
      description +=
        '. Shared image in use (a single large image will be generated and smartly cropped to all ad sizes)';
    }

    parts.push(description);
  }

  if (setup.imageSmartCrop) {
    const crop = setup.imageSmartCrop;
    parts.push(
      `Image Smart Crop (object to detect in source image: ${crop.objectDetectPrompt || 'N/A'}, margin: ${crop.marginPercent}%, Extend Image with AI if needed: ${crop.isAiExtendImageIfNeeded ?? false})`,
    );
  }

  if (setup.productShotGeneration) {
    const prod = setup.productShotGeneration;
    const productTypeName =
      prod.productType === ProductShotType.Vehicle ? 'Vehicle' : 'Product';

    const sceneSource = prod.ScenePromptInputColumnName?.length
      ? 'prompt'
      : prod.OptionalSceneReferenceImageInputColumnName
        ? 'reference image'
        : 'N/A';

    let description = `Product Shot Generation (productType: ${productTypeName}. Scene from ${sceneSource}. margin: ${prod.marginPercent}%)`;

    if (prod.useSharedIntermediateImage) {
      description +=
        '. Shared image in use (a single large image will be generated and smartly cropped to all ad sizes)';
    }

    parts.push(description);
  }

  return parts.length > 0 ? parts.join(' + ') : 'Not configured';
}

function formatContentFeedColumnsSection(project: RemoteAiContentProject): string {
  const contentFeedColumns = project.templateMetadata?.contentFeedColumns?.columns;

  if (!contentFeedColumns || Object.keys(contentFeedColumns).length === 0) {
    return '### Project Planned output deliverables\nNo content feed columns defined.';
  }

  const taxonomyMapping = taxonomy.taxonomyMappingsSubject.data?.[TaxonomyType.Content] || {};

  const lines: string[] = [
    '### Project Planned output deliverables (Which content feed columns will be generated, based on the template mapping to content feed)',
  ];
  lines.push('Note: this include the generation settings for each output column');
  lines.push(
    '| # | Column Key | Name | Type | Purpose | Input Columns Participate in Generation | Generation Setup |',
  );
  lines.push(
    '|---|------------|------|------|---------|----------------------------------------|------------------|',
  );

  let index = 1;

  Object.entries(contentFeedColumns).forEach(([key, params]: [string, RemoteAiContentContentFeedColumnParams]) => {
    const typeName = DYNAMIC_PARAM_TYPE_LABELS[params.type] || 'Unknown';
    const friendlyName = taxonomize(taxonomyMapping, key, key);
    const inputColumns = getInputColumnsFromSetup(params.generationSetup);
    const setupSummary = formatGenerationSetupSummary(params.generationSetup);
    lines.push(
      `| ${index} | ${key} | ${friendlyName} | ${typeName} | ${params.purpose || 'Not specified'} | ${inputColumns} | ${setupSummary} |`,
    );
    index++;
  });

  return lines.join('\n');
}

function formatFeaturesSection(templateSummary: RemoteTemplateSummary | null | undefined): string {
  const features = templateSummary?.features;

  if (!features || features.length === 0) {
    return '### Template Features\nNo special features configured.';
  }

  const featureNames = features.join(', ');

  return `### Template Features\nThis template supports: ${featureNames}`;
}

function formatItemsSummarySection(
  project: RemoteAiContentProject,
  items: RemoteAiContentItem[],
): string {
  const columns = project.variationInputSchema?.columns || [];
  const columnNames = columns.map((c: RemoteAiContentVariationColumn) => c.name || 'Unnamed').join(', ');

  const lines: string[] = ['### Items Summary'];
  lines.push(`- Total Items: ${items.length}`);
  lines.push(`- Items Table Columns: id, name${columnNames ? ', ' + columnNames : ''}`);

  return lines.join('\n');
}

function formatItemsDataSection(
  project: RemoteAiContentProject,
  items: RemoteAiContentItem[],
  assetLookup: Record<number, AssetInfo>,
): string {
  if (items.length === 0) {
    return '### Items Data\nNo items created yet.';
  }

  const columns = project.variationInputSchema?.columns || [];
  const columnNames = columns.map((c: RemoteAiContentVariationColumn) => c.name || 'unnamed');

  const imageVideoColumnNames = new Set(
    columns
      .filter(
        (c: RemoteAiContentVariationColumn) =>
          c.dataType === AiContentVariationColumnDataType.Image ||
          c.dataType === AiContentVariationColumnDataType.Video,
      )
      .map((c: RemoteAiContentVariationColumn) => c.name || 'unnamed'),
  );

  const lines: string[] = ['### Items Data (CSV)'];

  // Header row
  const headerRow = ['id', 'name', 'status', ...columnNames].join(',');
  lines.push(headerRow);

  // Data rows
  items.forEach((item) => {
    const inputValues = columnNames.map((colName: string) => {
      const value = item.inputValues[colName];

      if (value === null || value === undefined) {
        return '';
      }

      // Resolve Image/Video columns to asset name + ID
      if (imageVideoColumnNames.has(colName)) {
        const assetId = Number(value);
        const asset = !isNaN(assetId) ? assetLookup[assetId] : null;

        if (asset) {
          const formatted = `${asset.name} (ID: ${asset.id})`;

          return formatted.includes(',') || formatted.includes('"')
            ? `"${formatted.replace(/"/g, '""')}"`
            : formatted;
        }
      }

      // Escape quotes and wrap in quotes if contains comma
      const stringValue = String(value);

      if (stringValue.includes(',') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    });

    const itemStatus = getItemGenerationStatus(item);

    const nameEscaped = item.name.includes(',') || item.name.includes('"')
      ? `"${item.name.replace(/"/g, '""')}"`
      : item.name;

    const row = [item.id, nameEscaped, itemStatus, ...inputValues].join(',');
    lines.push(row);
  });

  return lines.join('\n');
}

function aiContentProjectContext(store: CLStore): AgentContext<AiContentProjectContextData> {
  function getter(): AiContentProjectContextData {
    const state = store.getState();
    const project = state.aiContentProjectPage?.project ?? null;
    const allPersonas: Persona[] = state.brandGuidelinesPage?.brandPersonas?.data ?? [];

    // Filter personas to only those assigned to this project
    const projectPersonaIds = project?.brief?.personaIds ?? [];
    const projectPersonas: PersonaInfo[] = allPersonas
      .filter((persona) => projectPersonaIds.includes(persona.id))
      .map((persona) => ({
        id: persona.id,
        name: persona.name,
      }));

    // Build asset ID to name lookup from project assets tree
    const projectAssetsTree: Folder | null =
      state.aiContentProjectPage?.projectAssets?.assetTree ?? null;
    const assetLookup: Record<number, AssetInfo> = {};

    if (projectAssetsTree) {
      getAllAssetsFromTree(projectAssetsTree).forEach((asset) => {
        assetLookup[asset.id] = {id: asset.id, name: asset.name || 'Unnamed'};
      });
    }

    return {
      project,
      items: state.aiContentProjectPage?.items ?? [],
      projectPersonas,
      assetLookup,
    };
  }

  function formatter({
    currentContext,
  }: {
    prevContext: AiContentProjectContextData | null;
    currentContext: AiContentProjectContextData;
  }): string | null {
    const {project, items, projectPersonas, assetLookup} = currentContext;

    if (!project) {
      return 'The user is not currently viewing an AI Content Project.';
    }

    const sections = [
      `## AI Content Project: ${project.name || 'Unnamed'} (ID: ${project.id})`,
      '',
      formatBriefSection(project, projectPersonas),
      '',
      formatInputParametersSection(project),
      '',
      formatTemplateSection(project),
      '',
      formatContentFeedColumnsSection(project),
      '',
      formatFeaturesSection(project.templateSummary),
      '',
      formatItemsSummarySection(project, items),
      '',
      formatItemsDataSection(project, items, assetLookup),
    ];

    return sections.join('\n');
  }

  return {
    title: 'AI Content Project',
    getter,
    formatter,
  };
}

export default aiContentProjectContext;
